import { useState } from 'react';
import {
  documentV3Schema,
  removeValueAtPath,
  setValueAtPath,
  type DocumentV3,
} from '@docgen/schema';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { cn } from '../lib/utils';

/**
 * Structured editor over `doc.data` — the same tree every `$bind` points at.
 * Objects are collapsible groups, arrays are row editors, scalars are
 * inputs. Raw JSON stays available behind a toggle for shape changes.
 */
export interface DataEditorProps {
  doc: DocumentV3;
  onChange: (next: DocumentV3, coalesceKey?: string) => void;
}

const LONG_TEXT = 60;

function titleize(key: string): string {
  return key.replace(/[-_.]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
}

function ScalarInput({ value, onCommit }: { value: unknown; onCommit: (v: unknown) => void }) {
  if (typeof value === 'boolean') {
    return (
      <input type="checkbox" checked={value} onChange={(e) => onCommit(e.target.checked)} />
    );
  }
  if (typeof value === 'number') {
    return (
      <Input
        type="number"
        value={value}
        className="h-7 text-xs"
        onChange={(e) => onCommit(e.target.value === '' ? 0 : Number(e.target.value))}
      />
    );
  }
  const text = value === null || value === undefined ? '' : String(value);
  if (text.length > LONG_TEXT || text.includes('<')) {
    return (
      <Textarea
        value={text}
        rows={Math.min(6, Math.max(2, Math.ceil(text.length / 60)))}
        className="text-xs"
        onChange={(e) => onCommit(e.target.value)}
      />
    );
  }
  return <Input value={text} className="h-7 text-xs" onChange={(e) => onCommit(e.target.value)} />;
}

function Node({ path, value, label, depth, set, removeAt }: {
  path: string;
  value: unknown;
  label: string;
  depth: number;
  set: (path: string, v: unknown) => void;
  removeAt: (path: string) => void;
}) {
  const [open, setOpen] = useState(depth < 1);

  if (Array.isArray(value)) {
    return (
      <div className="space-y-1">
        <button
          type="button"
          className="flex w-full items-center gap-1 text-left text-xs font-semibold capitalize text-foreground/80 hover:text-foreground"
          onClick={() => setOpen(!open)}
        >
          {open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          {titleize(label)}
          <span className="font-normal text-muted-foreground">({value.length})</span>
        </button>
        {open && (
          <div className="space-y-1.5 border-l pl-2">
            {value.map((item, i) => (
              <div key={i} className="rounded-md border bg-background/60 p-1.5">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-[10px] font-semibold text-muted-foreground">#{i + 1}</span>
                  <button
                    type="button"
                    aria-label="Remove entry"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => removeAt(`${path}.${i}`)}
                  >
                    <Trash2 className="size-3" />
                  </button>
                </div>
                <Node
                  path={`${path}.${i}`}
                  value={item}
                  label={String(i)}
                  depth={depth + 1}
                  set={set}
                  removeAt={removeAt}
                />
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              className="h-6 text-[11px]"
              onClick={() => {
                const template = value[value.length - 1];
                const blank =
                  template !== null && typeof template === 'object' && !Array.isArray(template)
                    ? Object.fromEntries(Object.keys(template).map((k) => [k, '']))
                    : '';
                set(`${path}.${value.length}`, blank);
              }}
            >
              <Plus className="!size-3" /> Add
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    const isRow = depth > 0 && /^\d+$/.test(label);
    return (
      <div className={cn('space-y-1.5', !isRow && depth > 0 && 'border-l pl-2')}>
        {!isRow && depth > 0 && (
          <p className="text-xs font-semibold capitalize text-foreground/80">{titleize(label)}</p>
        )}
        {entries.map(([k, v]) => (
          <Node
            key={k}
            path={path ? `${path}.${k}` : k}
            value={v}
            label={k}
            depth={depth + 1}
            set={set}
            removeAt={removeAt}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      <label className="text-[10px] font-medium capitalize text-muted-foreground">{titleize(label)}</label>
      <ScalarInput value={value} onCommit={(v) => set(path, v)} />
    </div>
  );
}

export function DataEditor({ doc, onChange }: DataEditorProps) {
  const [showJson, setShowJson] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const set = (path: string, v: unknown) => {
    onChange({ ...doc, data: setValueAtPath(doc.data, path, v) }, `data:${path}`);
  };
  const removeAt = (path: string) => {
    onChange({ ...doc, data: removeValueAtPath(doc.data, path) });
  };

  if (showJson) {
    return (
      <div className="space-y-2">
        <Textarea
          rows={24}
          className="font-mono text-[11px]"
          value={draft ?? JSON.stringify(doc.data, null, 2)}
          onChange={(e) => setDraft(e.target.value)}
          spellCheck={false}
        />
        <div className="flex items-center gap-1.5">
          <Button size="sm" disabled={draft === null}
            onClick={() => {
              try {
                const data = documentV3Schema.shape.data.parse(JSON.parse(draft ?? ''));
                onChange({ ...doc, data });
                setDraft(null);
                setError(null);
                setShowJson(false);
              } catch (e) {
                setError(e instanceof Error ? e.message.slice(0, 140) : 'invalid JSON');
              }
            }}>
            Apply
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setDraft(null); setError(null); setShowJson(false); }}>
            Cancel
          </Button>
        </div>
        {error && <p className="text-[11px] text-destructive">{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {Object.entries(doc.data).map(([k, v]) => (
        <Node key={k} path={k} value={v} label={k} depth={0} set={set} removeAt={removeAt} />
      ))}
      <Button variant="ghost" size="sm" className="text-[11px]" onClick={() => setShowJson(true)}>
        Edit as JSON
      </Button>
    </div>
  );
}
